import liveServerStatus from '../../liveServerStatus';

/**
 * Generate HTML email template for task assignment
 * @param {Object} taskData - Task details
 * @param {Object} assignerData - Information about the person assigning the task
 * @param {Object} assigneeData - Information about the person receiving the task
 * @returns {string} - HTML email content
 */
export const generateTaskAssignmentEmailHTML = (taskData, assignerData, assigneeData) => {
  const priorityColors = {
    Low: '#4caf50',
    Medium: '#2196f3',
    High: '#ff9800',
    Urgent: '#f44336'
  };

  const priorityColor = priorityColors[taskData.priority] || '#2196f3';
  
  const formattedDueDate = new Date(taskData.endDate).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Get first name for greeting
  const firstName = assigneeData.name ? assigneeData.name.split(' ')[0] : 'there';

  // Add test environment indicator in the header if not in live environment
  const testEnvironmentBanner = !liveServerStatus ? `
    <div style="background-color: #ffeb3b; color: #333; text-align: center; padding: 5px; font-weight: bold; font-size: 12px;">
      ⚠️ TEST EMAIL - No action required ⚠️
    </div>
  ` : '';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>New Task Assignment</title>
      <style>
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          line-height: 1.6;
          color: #333;
          margin: 0;
          padding: 0;
          background-color: #f5f5f5;
        }
        .email-container {
          max-width: 600px;
          margin: 0 auto;
          background-color: #ffffff;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
        }
        .email-header {
          background-color: #424242;
          color: white;
          padding: 20px;
          text-align: center;
        }
        .email-header h1 {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
        }
        .email-body {
          padding: 30px;
        }
        .greeting {
          font-size: 18px;
          font-weight: 500;
          margin-bottom: 20px;
          color: #212121;
        }
        .task-details {
          background-color: #f5f5f5;
          border-radius: 6px;
          padding: 20px;
          margin-bottom: 25px;
          border: 1px solid #e0e0e0;
        }
        .task-title {
          font-size: 20px;
          font-weight: 600;
          margin-bottom: 12px;
          color: #212121;
        }
        .task-description {
          color: #424242;
          margin-bottom: 20px;
        }
        .task-info {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 5px;
        }
        .task-info-item {
          flex: 1;
          min-width: 140px;
        }
        .task-info-label {
          font-size: 12px;
          text-transform: uppercase;
          color: #757575;
          margin-bottom: 4px;
        }
        .task-info-value {
          font-weight: 500;
          color: #212121;
        }
        .priority-tag {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 16px;
          background-color: ${priorityColor}; 
          color: white;
          font-size: 12px;
          font-weight: 500;
        }
        .divider {
          height: 1px;
          background-color: #e0e0e0;
          margin: 25px 0;
        }
        .cta-button {
          display: inline-block;
          background-color: #424242;
          color: white;
          text-decoration: none;
          padding: 12px 25px;
          border-radius: 4px;
          font-weight: 500;
          margin-top: 5px;
          text-align: center;
        }
        .message {
          margin-bottom: 25px;
          color: #424242;
        }
        .email-footer {
          background-color: #212121;
          padding: 20px;
          text-align: center;
          color: #f5f5f5;
          font-size: 12px;
        }
        .company-name {
          font-weight: 600;
          color: #f5f5f5;
          margin-bottom: 8px;
        }
      </style>
    </head>
    <body>
      ${testEnvironmentBanner}
      <div class="email-container">
        <div class="email-header">
          <h1>New Task Assigned to You</h1>
        </div>
        
        <div class="email-body">
          <div class="greeting">
            Hello ${firstName},
          </div>
          
          <div class="message">
            You have been assigned a new task by <strong>${assignerData.name || assignerData.email}</strong>. Please review the details below:
          </div>
          
          <div class="task-details">
            <div class="task-title">${taskData.title}</div>
            
            ${taskData.description ? `<div class="task-description">${taskData.description}</div>` : ''}
            
            <div class="task-info">
              <div class="task-info-item">
                <div class="task-info-label">Priority</div>
                <div class="task-info-value">
                  <span class="priority-tag" style="background-color: ${priorityColor};">${taskData.priority}</span>
                </div>
              </div>
              
              <div class="task-info-item">
                <div class="task-info-label">Due Date</div>
                <div class="task-info-value">${formattedDueDate}</div>
              </div>
            </div>
          </div>
          
          <a href="${process.env.REACT_APP_URL || 'https://revamped-ppc.myelinz.com'}/my-tasks" class="cta-button">View Task Details</a>
          
          <div class="divider"></div>
          
          <div class="message">
            Please ensure that you complete this task by the due date. If you have any questions or need clarification, please reply to this email or contact ${assignerData.name || 'your manager'} directly.
          </div>
        </div>
        
        <div class="email-footer">
          <div class="company-name">MyelinZ PPC Portal</div>
          <p>This is an automated message. Please do not reply directly to this email.</p>
          <p>© ${new Date().getFullYear()} MyelinZ. All rights reserved.</p>
          ${!liveServerStatus ? '<p><strong>TEST ENVIRONMENT - No action required</strong></p>' : ''}
        </div>
      </div>
    </body>
    </html>
  `;
};

export default generateTaskAssignmentEmailHTML; 